/**
 * @module utils/customColumnConfigRename
 * @description 自定义字段改名时，迁移图表设置里所有「按 column_name 引用字段」的配置项
 *
 * ## 为什么需要这个模块？
 *
 * 1. **改名入口在 DVA model，配置在 ChartSettings Context**
 *    - 用户在左侧「字段定义」改 `name_cn` → `updateCustomColumn` 原地改 QB 里 IColumnInfo.column_name（唯一身份为 name_cn）
 *    - 面板配置（description、field_setting、statistics 等）存在 Context base/draft，key 多为 `t0_{column_name}`
 *    - 若不迁移，旧 key（如 `t0_e`）会残留，新名没有配置，计算/保存仍指向旧名
 *
 * 2. **不能只依赖 QB 联动管道（useQBLinkagePipeline）**
 *    - 联动靠 prev/next 快照 diff 出 `fieldIdentityRenamed`，但 model 原地 mutate 会导致 prev 快照已被污染（见 freezeQBSnapshot）
 *    - 联动还有 ~500ms debounce，改名瞬间用户已可能在看配置
 *    - 部分 handler 原先未实现 migrate（如 conditional_format 仅 prune）
 *    - 因此在 `updateCustomColumn` 里用**同步、确定性**的 oldColumnName → newColumnName 迁移（本模块）
 *
 * 3. **维度和指标要分开处理**
 *    - 维度：`field_setting`、description 后缀 `_1` 等
 *    - 指标：statistics、conditional_format、双 Y 轴、排序 series（chartSorts / customMetricSort / series_sorts）等
 *    - 二者都要改：description、column_sort、crosstab 等
 *    - 字段排序（datasetField）自定义字段：`chartSorts[].key` 用 name_cn，`table_code` 空/t0（上传兜底 t0），即使未进 QB 也迁
 *
 * 4. **双写：Context + DVA save_column_infos**
 *    - 计算走 Context `convert(base)`；进页又从 `save_column_infos.chartsCfgSave` 灌入 base
 *    - 只改 Context 时 devtools 里 Redux 仍是旧 key；只改 DVA 时面板仍是旧 key → 两处都要迁
 *
 * 规则与 `linkage/qbFields/qbLinkage/handlers/*` 保持一致，避免「管道迁了、改名入口没迁」两套逻辑分叉。
 *
 * @see model.ts updateCustomColumn
 * @see chartSettingsBridge.applyCustomColumnRenameOnQB
 * @see docs/qb-to-panel-sync-design.md 类别 C：字段改名
 */

import { ConfigChartType, type TChartType } from '@dbl/core/chart';
import { mergeConfigLayer } from './mergeConfigPatch';
import { migrateDescriptionIdentity } from '../linkage/qbFields/qbLinkage/handlers/shared';
import type { IFieldSnapshot } from '../linkage/qbFields/qbLinkage/types';
import { migrateColumnSortIdentity } from '../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/syncOps';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  syncCrosstabFromQBChange,
  type ICrosstabConfig,
} from './crosstab';
import {
  buildRecordSubKeyPatch,
  migrateFieldSettingRecord,
} from './fieldSettingRename';
import { migrateMetricSortRows } from '../linkage/qbFields/qbLinkage/handlers/seriesSorts';

export type TCustomColumnBuilderType = 'dimension' | 'metric';

/** 改名参数：old/new 为自定义字段唯一身份 name_cn（与 QB column_name / chartSorts.key 一致） */
export interface ICustomColumnRenameParams {
  tableCode: string;
  tableId: string;
  oldColumnName: string;
  newColumnName: string;
  /** 字段在 QB 操作区是维度还是指标，决定迁哪些配置项 */
  builderType: TCustomColumnBuilderType;
  columnDisplay?: string;
  columnDisplayEn?: string;
}

/** 构造与 QB 联动相同的 IFieldSnapshot，供 column_sort / crosstab 等复用既有 migrate 函数 */
function toFieldSnap(params: ICustomColumnRenameParams): IFieldSnapshot {
  return {
    id: '',
    table_id: params.tableId,
    table_code: params.tableCode || 't0',
    column_name: params.newColumnName,
    column_display: params.columnDisplay || params.newColumnName,
    column_display_en: params.columnDisplayEn || params.newColumnName,
    builder_type: params.builderType,
    column_custom: true,
  };
}

function syntheticIdentityRename(params: ICustomColumnRenameParams) {
  const snap = toFieldSnap(params);
  return {
    field: snap,
    prevColumnName: params.oldColumnName,
    nextColumnName: params.newColumnName,
    displayOnly: false,
  };
}

function remapStatisticValue(
  v: string,
  oldName: string,
  newName: string,
): { value: string; changed: boolean; } {
  if (v === oldName) {
    return { value: newName, changed: true };
  }
  return { value: v, changed: false };
}

function migrateStatistics(
  statistics: Record<string, string[]> | undefined,
  oldName: string,
  newName: string,
): Record<string, string[]> | undefined {
  if (!statistics) {
    return undefined;
  }
  const next: Record<string, string[]> = {};
  let changed = false;
  for (const [key, values] of Object.entries(statistics)) {
    const mapped: string[] = [];
    for (const v of values || []) {
      const { value, changed: vc } = remapStatisticValue(v, oldName, newName);
      if (vc) {
        changed = true;
      }
      mapped.push(value);
    }
    if (mapped.length) {
      next[key] = mapped;
    }
  }
  return changed ? next : undefined;
}

type TFormatRow = { metric?: string; id?: string; };

function remapConditionalFormatRow(
  item: TFormatRow,
  oldName: string,
  newName: string,
): { item: TFormatRow; changed: boolean; } {
  const name = item.metric ?? item.id;
  if (name && String(name) === oldName) {
    return {
      item: {
        ...item,
        metric: newName,
        id: item.id === oldName ? newName : item.id,
      },
      changed: true,
    };
  }
  return { item, changed: false };
}

function migrateConditionalFormat(
  cf: Record<string, unknown> | undefined,
  oldName: string,
  newName: string,
): Record<string, unknown> | undefined {
  if (!cf) {
    return undefined;
  }
  const next = { ...cf };
  let changed = false;
  for (const listKey of ['table_bar_percentage', 'table_numerical_values']) {
    const items = next[listKey] as TFormatRow[] | undefined;
    if (!Array.isArray(items)) {
      continue;
    }
    const mapped: TFormatRow[] = [];
    for (const item of items) {
      const { item: nextItem, changed: rowChanged } = remapConditionalFormatRow(
        item,
        oldName,
        newName,
      );
      if (rowChanged) {
        changed = true;
      }
      mapped.push(nextItem);
    }
    if (changed) {
      next[listKey] = mapped;
    }
  }
  return changed ? next : undefined;
}

type TPercentageRow = { metric: string; table_code?: string; };

function migrateColumnPercentage(
  list: TPercentageRow[] | undefined,
  oldName: string,
  newName: string,
  tableCode: string,
): TPercentageRow[] | undefined {
  if (!list?.length) {
    return undefined;
  }
  let changed = false;
  const next = list.map(row => {
    if (
      row.metric === oldName &&
      (row.table_code || 't0') === (tableCode || 't0')
    ) {
      changed = true;
      return { ...row, metric: newName };
    }
    return row;
  });
  return changed ? next : undefined;
}

/**
 * 「维度占比」列重命名迁移（与后端 dimension_percentage 契约同构）— 更新于 2026-06-25：
 * - 结构为 `{ metrics: [{ metric, label, group_by:[{ table_code, column }] }] }`
 *   group_by 不在根级，而是写在每条 metrics[] 内
 * - metrics[].metric 改名（要求 table_code 匹配）→ 同步 metric 引用
 * - metrics[].group_by[].column 改名 → 同步 column 引用
 * - label 是用户展示名，不需要迁移
 */
type TGroupByRow = { table_code?: string; column?: string; };
type TMetricRow = {
  table_code?: string;
  metric?: string;
  label?: string;
  group_by?: TGroupByRow[];
};
type TDimensionPercentageConfig = {
  metrics?: TMetricRow[];
};

function migrateDimensionPercentage(
  config: TDimensionPercentageConfig | undefined,
  oldName: string,
  newName: string,
  tableCode: string,
): TDimensionPercentageConfig | undefined {
  if (!config || typeof config !== 'object' || !oldName || !newName) {
    return undefined;
  }
  const metrics = Array.isArray(config.metrics) ? [...config.metrics] : [];
  let changed = false;

  const nextMetrics = metrics.map(m => {
    let rowChanged = false;
    // 1) metrics[].metric 改名（要求 table_code 匹配）
    let nextMetric = m.metric;
    if (
      m?.metric === oldName &&
      (m.table_code || 't0') === (tableCode || 't0')
    ) {
      nextMetric = newName;
      rowChanged = true;
    }
    // 2) metrics[].group_by[].column 改名
    const groupBy = Array.isArray(m?.group_by) ? m.group_by : [];
    const nextGroupBy = groupBy.map(g => {
      if (g?.column === oldName) {
        rowChanged = true;
        return { ...g, column: newName };
      }
      return g;
    });
    if (rowChanged) {
      changed = true;
      return { ...m, metric: nextMetric, group_by: nextGroupBy };
    }
    return m;
  });

  return changed ? { metrics: nextMetrics } : undefined;
}

type TAxisConfig = {
  metric?: string;
  metrics?: Array<{ metric: string; tableCode?: string; table_code?: string; }>;
};

function migrateAxisConfig(
  axis: unknown,
  oldName: string,
  newName: string,
  tableCode: string,
): TAxisConfig | undefined {
  if (!axis || typeof axis !== 'object') {
    return undefined;
  }
  const src = axis as TAxisConfig;
  const next: TAxisConfig = { ...src };
  let changed = false;
  if (next.metric === oldName) {
    next.metric = newName;
    changed = true;
  }
  if (Array.isArray(next.metrics)) {
    const metrics = next.metrics.map(m => {
      const tc = m.tableCode ?? m.table_code ?? 't0';
      if (m.metric === oldName && tc === (tableCode || 't0')) {
        changed = true;
        return { ...m, metric: newName };
      }
      return m;
    });
    next.metrics = metrics;
  }
  return changed ? next : undefined;
}

function migrateTooltipSetting(
  tooltip: Record<string, unknown> | undefined,
  oldName: string,
  newName: string,
): Record<string, unknown> | undefined {
  if (!tooltip) {
    return undefined;
  }
  const extra = tooltip.tooltipExtra as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(extra)) {
    return undefined;
  }
  let changed = false;
  const mapped = extra.map(row => {
    const name = String(row.metric ?? row.column ?? row.column_name ?? '');
    if (name === oldName) {
      changed = true;
      return {
        ...row,
        metric: row.metric !== undefined ? newName : row.metric,
        column: row.column !== undefined ? newName : row.column,
        column_name: row.column_name !== undefined ? newName : row.column_name,
      };
    }
    return row;
  });
  return changed ? { ...tooltip, tooltipExtra: mapped } : undefined;
}

/**
 * 字段排序自定义字段改名：迁 chartSorts[].key（table_code 空或 t0），不写 customMetricSort。
 *
 * 自定义字段上传 table_code 兜底 t0；与指标排序 migrateMetricSortRows 分离。
 */
export function migrateDatasetFieldChartSorts(
  chartSorts: unknown,
  sortBy: unknown,
  oldColumnName: string,
  newColumnName: string,
): unknown {
  if (
    !oldColumnName
    || !newColumnName
    || oldColumnName === newColumnName
    || !Array.isArray(chartSorts)
    || !chartSorts.length
  ) {
    return undefined;
  }
  const mode = String(sortBy ?? '');
  if (mode !== 'datasetField' && mode !== 'datasetFieldSort') {
    return undefined;
  }

  let changed = false;
  const next = chartSorts.map((row) => {
    if (!row || typeof row !== 'object') {
      return row;
    }
    const r = row as { key?: string; table_code?: string };
    // 自定义字段：空串或 t0（上传兜底）；其它表码视为物理表字段不迁
    if (r.table_code && r.table_code !== 't0') {
      return row;
    }
    if (r.key !== oldColumnName) {
      return row;
    }
    changed = true;
    return { ...r, key: newColumnName, table_code: 't0' };
  });
  return changed ? next : undefined;
}

/**
 * 对单层配置（base / draft / save_column_infos 内层）生成 MERGE_QB_PATCH 用的 patch
 *
 * 只返回有变化的 top-level key；Record 型（description、field_setting）用 buildRecordSubKeyPatch，
 * 以便 mergeConfigLayer 能 `undefined` 删除旧 sub-key，而不是浅合并后旧 key 残留。
 */
export function migrateConfigLayerOnCustomColumnRename(
  layer: Record<string, unknown>,
  params: ICustomColumnRenameParams,
): Record<string, unknown> | undefined {
  const {
    oldColumnName,
    newColumnName,
    tableCode,
    tableId,
    builderType,
  } = params;
  const canMigrateIdentity =
    !!oldColumnName && !!newColumnName && oldColumnName !== newColumnName;

  if (!canMigrateIdentity) {
    return undefined;
  }

  const patch: Record<string, unknown> = {};
  const snap = toFieldSnap(params);
  const roleSuffix = builderType === 'dimension' ? '1' : '0';

  // description：key 为 t0_{name} 与 t0_{name}_{0|1}，0=指标 1=维度
  {
    const descPrev = layer.description as Record<string, unknown> | undefined;
    const descNext = migrateDescriptionIdentity(descPrev, [
      { snap, prevColumnName: oldColumnName, roleSuffix },
    ]);
    const descPatch = buildRecordSubKeyPatch(descPrev, descNext);
    if (descPatch) {
      patch.description = descPatch;
    }
  }

  // field_setting 仅表格维度 URL 格式，key 规则与 UrlSetting 一致
  if (builderType === 'dimension') {
    const fsPrev = layer.field_setting as Record<string, unknown> | undefined;
    const fsNext = migrateFieldSettingRecord(
      fsPrev,
      oldColumnName,
      newColumnName,
      tableCode,
      tableId,
    );
    const fsPatch = buildRecordSubKeyPatch(fsPrev, fsNext);
    if (fsPatch) {
      patch.field_setting = fsPatch;
    }
  }

  // dimension_percentage：metrics[].metric 与 metrics[].group_by[].column 都按 table_code 匹配迁移
  // （不论 builderType 是维度还是指标，只要该 column 在配置中引用了，就迁移）
  {
    const dpNext = migrateDimensionPercentage(
      layer.dimension_percentage as TDimensionPercentageConfig | undefined,
      oldColumnName,
      newColumnName,
      tableCode,
    );
    if (dpNext) {
      patch.dimension_percentage = dpNext;
    }

    // statistics：值为列名数组，按字符串替换（多用于指标）
    const statsNext = migrateStatistics(
      layer.statistics as Record<string, string[]> | undefined,
      oldColumnName,
      newColumnName,
    );
    if (statsNext) {
      patch.statistics = statsNext;
    }
  }

  // 字段排序：自定义字段 chartSorts[].key（=name_cn）迁移；不双写 customMetricSort
  // 未进 QB 时也可能只有这一项需要迁
  {
    const dfsNext = migrateDatasetFieldChartSorts(
      layer.chartSorts,
      layer.sortBy,
      oldColumnName,
      newColumnName,
    );
    if (dfsNext) {
      patch.chartSorts = dfsNext;
    }
  }

  const isDatasetFieldSort =
    layer.sortBy === 'datasetField' || layer.sortBy === 'datasetFieldSort';

  // 以下主要为「指标区」自定义字段的配置
  if (canMigrateIdentity && builderType === 'metric') {
    const cfNext = migrateConditionalFormat(
      layer.conditional_format as Record<string, unknown> | undefined,
      oldColumnName,
      newColumnName,
    );
    if (cfNext) {
      patch.conditional_format = cfNext;
    }

    const pctNext = migrateColumnPercentage(
      layer.column_percentage as TPercentageRow[] | undefined,
      oldColumnName,
      newColumnName,
      tableCode,
    );
    if (pctNext) {
      patch.column_percentage = pctNext;
    }

    // 图例按指标排序 + X 轴 chartSorts/customMetricSort：行内用 key 引用 QB 指标
    // 字段排序模式已在上方单独迁 chartSorts，禁止再双写 customMetricSort
    for (const key of ['chartSorts', 'customMetricSort', 'series_sorts'] as const) {
      if (isDatasetFieldSort && (key === 'chartSorts' || key === 'customMetricSort')) {
        continue;
      }
      const sorted = migrateMetricSortRows(
        layer[key],
        oldColumnName,
        newColumnName,
        tableCode,
      );
      if (sorted) {
        patch[key] = sorted;
      }
    }

    const primary = migrateAxisConfig(
      layer.primaryYAxis,
      oldColumnName,
      newColumnName,
      tableCode,
    );
    if (primary) {
      patch.primaryYAxis = primary;
    }
    const second = migrateAxisConfig(
      layer.secondYAxis,
      oldColumnName,
      newColumnName,
      tableCode,
    );
    if (second) {
      patch.secondYAxis = second;
    }

    const tooltipNext = migrateTooltipSetting(
      layer.tooltipSetting as Record<string, unknown> | undefined,
      oldColumnName,
      newColumnName,
    );
    if (tooltipNext) {
      patch.tooltipSetting = tooltipNext;
    }
  }

  // column_sort：维/指均可，按 item.column + table_code 匹配后改 column
  if (canMigrateIdentity) {
    const sortList = layer.column_sort as IColumnSortItem[] | undefined;
    if (Array.isArray(sortList) && sortList.length) {
      const sortNext = migrateColumnSortIdentity(sortList, [
        syntheticIdentityRename(params),
      ]);
      if (JSON.stringify(sortNext) !== JSON.stringify(sortList)) {
        patch.column_sort = sortNext;
      }
    }

    // crosstab：rows/columns 里存 { table_code, column }，与 QB 联动同一套 migrateRefs
    const crosstabExisting = layer.crosstab as ICrosstabConfig | undefined;
    if (crosstabExisting) {
      const crosstabNext = syncCrosstabFromQBChange({
        existing: crosstabExisting,
        columnDimensions: [],
        columnMetrics: [],
        addedDimensions: [],
        addedMetrics: [],
        removedDimensions: [],
        removedMetrics: [],
        identityRenames: [syntheticIdentityRename(params)],
      });
      if (crosstabNext) {
        patch.crosstab = crosstabNext;
      }
    }
  }

  return Object.keys(patch).length ? patch : undefined;
}

/**
 * 同步 DVA `save_column_infos.chartsCfgSave[chartType]`
 *
 * 用户若在 Redux devtools 看 save_column_infos，或进页/计算前从 DVA 补配置，
 * 也需要看到迁移后的 key，否则会出现「Context 对了、持久化仍是旧名」的割裂。
 */
export function migrateSaveColumnInfosOnCustomColumnRename(
  saveColumnInfos: unknown,
  chartType: TChartType,
  params: ICustomColumnRenameParams,
): unknown {
  if (!saveColumnInfos || typeof saveColumnInfos !== 'object') {
    return saveColumnInfos;
  }
  const root = saveColumnInfos as Record<string, unknown>;
  const chartsCfgSave = root.chartsCfgSave as
    | Record<string, { customizeSetting?: Record<string, Record<string, unknown>>; }>
    | undefined;
  const typeKey = chartType as string;
  const storageKey = ConfigChartType[chartType];
  const wrapper = chartsCfgSave?.[typeKey];
  const inner = wrapper?.customizeSetting?.[storageKey];
  if (!inner || typeof inner !== 'object') {
    return saveColumnInfos;
  }
  const patch = migrateConfigLayerOnCustomColumnRename(inner, params);
  if (!patch) {
    return saveColumnInfos;
  }
  const migratedInner = mergeConfigLayer(
    { ...(inner as Record<string, unknown>) },
    patch,
  );
  return {
    ...root,
    chartsCfgSave: {
      ...chartsCfgSave,
      [typeKey]: {
        ...wrapper,
        customizeSetting: {
          ...wrapper!.customizeSetting,
          [storageKey]: migratedInner,
        },
      },
    },
  };
}

